import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useHistory, useParams, useRouteMatch } from 'react-router';
import { Redirect } from 'react-router-dom';
import { addKey } from 'actions/anonymousEditingKeys';
import * as urls from 'constants/urls';
import NodesView from 'components/NodesView';
import { addSessionMatrix } from '../actions/matrices';
import './MatrixChartContainer.scss';
import ConnectionsView from 'components/connectionsView/ConnectionsView';
import { useIdleTimer } from 'react-idle-timer';
import { MenuTabs } from 'components/menus/MenuTabs';
import { Tab } from 'components/menus/Tab';
import NodeSettings from 'components/nodes/NodeSettings/NodeSettings';
import { writePermissions } from 'constants/auth';
import { useIsInUse } from 'hooks/matrices';
import { duplicateMatrix } from 'constants/matrices';
import {
  createMatrix,
  saveMatrix,
  fetchMatrix,
  invalidateMatrix,
} from 'actions/matrices';
import { finishEdit } from 'actions/matrixSettings';
import { useNewExperiment } from 'hooks/experiments';
import PublicWarningModal from 'components/PublicWarningModal';

const MatrixChartContainer = ({
  matrix,
  base,
  viewMode,
  connectionsTabOpen,
  setConnectionsTabOpen,
  chartView,
  setChartView,
  //
  publicWarningDismissed,
  setPublicWarningDismissed,
  showPublicWarning,
  setShowPublicWarning,
  onCancelPublicWarning,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  let { url, path } = useRouteMatch();
  const { anonymousEditingKey } = useParams();

  const { user } = useSelector(state => state.auth);
  const { user_id } = user?.claims ?? { user_id: null };

  const { isAdmin } = useSelector(state => state.auth.userData);
  const newExperiment = useNewExperiment();

  const hasEditAccess = useMemo(
    () => writePermissions.includes(matrix?.roles?.[user_id]),
    [matrix, user_id]
  );
  const isInUse = useIsInUse({ id: matrix?.id, hasEditAccess });

  const matrixIsInUse = useMemo(() => {
    return (
      hasEditAccess &&
      !!isInUse &&
      isInUse?.user !== user_id &&
      isInUse?.user !== null
    );
  }, [user_id, isInUse, hasEditAccess]);

  const initialSessionMatrices = useSelector(
    state => state.matrices.sessionMatrices
  );

  const initialSessionMatrix = useMemo(() => {
    const id = matrix?.id;
    return initialSessionMatrices?.[id];
  }, [matrix, initialSessionMatrices]);

  useEffect(() => {
    if (anonymousEditingKey && matrix?.parentId) {
      dispatch(addKey(matrix.id, anonymousEditingKey));
      history.push(urls.experiment(matrix));
    }
  }, [anonymousEditingKey, dispatch, matrix, history]);

  const cancelUrl = useMemo(() => {
    return matrix?.parentId ? urls.experiment(matrix) : urls.matrix(matrix);
  }, [matrix]);

  const saveUrl = useMemo(() => {
    return matrix?.parentId ? urls.experiment(matrix) : urls.matrix(matrix);
  }, [matrix]);

  const nodes = useMemo(() => {
    return matrix?.nodes;
  }, [matrix]);

  const sessionMatrix = useMemo(() => {
    return matrix;
  }, [matrix]);

  // Run on mount matrix, do not update matrix again within session
  useEffect(() => {
    const sessionMatrixId = sessionMatrix?.id;
    if (sessionMatrixId && !initialSessionMatrix) {
      dispatch(addSessionMatrix(sessionMatrix));
    }
  }, [dispatch, sessionMatrix, initialSessionMatrix]);

  // If idled, unmount the component
  // This will trigger appropriate inUse behavior
  const onIdle = useCallback(() => {
    if (matrix?.id) dispatch(invalidateMatrix(matrix.id));
    if (base?.id) dispatch(invalidateMatrix(base.id));
    history.push('/gallery');
  }, [history, dispatch, matrix, base]);

  useIdleTimer({
    onIdle,
    element: document.getElementById('MpgMatrixApp'),
    timeout: 900_000,
    throttle: 500,
  });

  const menuOptions = useMemo(
    () => [
      {
        label: 'By Trophic Level',
        value: 'byTrophicLevel',
        active: connectionsTabOpen === false,
        icon: 'byTrophicLevel',
        className: 'tour-back-to-trophic-levels',
      },
      {
        label: 'By Connections',
        value: 'byConnections',
        active: connectionsTabOpen === true,
        icon: 'byConnections',
        className: 'interactiveTourGettingStarted-4',
      },
    ],
    [connectionsTabOpen]
  );

  const setView = value => {
    if (value === 'byConnections') {
      setConnectionsTabOpen(true);
    } else {
      setConnectionsTabOpen(false);
    }
  };

  // ------------------------------------------------------------------------------------------------------------------------

  const onDuplicateMatrixPublicWarning = useCallback(
    v => {
      setPublicWarningDismissed(v);
      setShowPublicWarning(false);

      if (!user) {
        const experiment = newExperiment(matrix);
        dispatch(fetchMatrix(experiment.id));
        const parentId = matrix.parentId ?? matrix.id;
        history.push(`/matrices/${parentId}/experiments/${experiment.id}`);
        return;
      }

      const toDuplicate = matrix;
      const duplicate = duplicateMatrix(toDuplicate, user);
      dispatch(finishEdit(toDuplicate));
      dispatch(createMatrix(duplicate));
      dispatch(saveMatrix(duplicate.id));

      const urlPath = matrix?.parentId
        ? `/matrices/${duplicate?.parentId}/experiments/${duplicate?.id}/edit`
        : `/matrices/${duplicate?.id}/edit`;

      // We have the matrix, but fetch anyway since it's all
      // saved and hasn't hit the loading list
      dispatch(fetchMatrix(duplicate.id));

      history.push(urlPath);
    },
    [dispatch, history, user, matrix, newExperiment]
  );

  const topDescriptiveText = useMemo(() => {
    let name = '';

    const isPublic = matrix?.isPublic;

    if (isPublic) {
      name = 'Public matrix';

      const {
        templateName = undefined,
        templateLocation = undefined,
        templateEcosystem = undefined,
      } = matrix;

      const templateAddOns = [
        templateName,
        templateLocation,
        templateEcosystem,
      ].filter(Boolean);

      if (templateAddOns.length) {
        name = `${name}: ${templateAddOns.join(' — ')}`;
      }
    } else {
      name = `Saved ${matrix?.parentId ? 'Experiment' : 'Matrix'}`;
    }

    return name;
  }, [matrix]);

  // ------------------------------------------------------------------------------------------------------------------------

  return nodes ? (
    <>
      <div
        id={`MatrixChartContainer-${matrix?.id}`}
        className="MatrixChartContainer interactiveTourGettingStarted-2 interactiveTourGettingStarted-8"
      >
        {!chartView ? (
          <div className="MatrixChartContainer-header">
            <div className="MatrixChartContainer-header-title">
              {topDescriptiveText}
            </div>
            <div className="MatrixChartContainer-button-container">
              <MenuTabs>
                {menuOptions.map((option, i) => (
                  <Tab
                    key={i}
                    className={option?.className}
                    label={option.label}
                    onClick={() => setView(option.value)}
                    selected={option.active}
                    icon={option.icon}
                  />
                ))}
              </MenuTabs>
            </div>
          </div>
        ) : null}

        {/* TODO edit mode should now relate to table */}
        {/* {viewMode === 'edit' && connectionsTabOpen ? ( */}
        {connectionsTabOpen ? (
          <ConnectionsView matrix={matrix} viewMode={viewMode} />
        ) : (
          <NodesView
            key={matrix?.id}
            matrix={matrix}
            base={base}
            viewMode={viewMode}
            chartView={chartView}
            setChartView={setChartView}
          />
        )}
        {!publicWarningDismissed && showPublicWarning ? (
          <PublicWarningModal
            onExit={() => setShowPublicWarning(false)}
            onContinue={v => onCancelPublicWarning(v)}
            duplicateMatrix={v => onDuplicateMatrixPublicWarning(v)}
            isAdmin={isAdmin}
            isPublic={matrix?.isPublic}
            isInUse={matrixIsInUse}
            isAnonymous={!user}
          />
        ) : null}
      </div>
      <Route path={`${path}/overrides/:node`}>
        <NodeSettings
          matrix={matrix}
          saveUrl={saveUrl}
          cancelUrl={cancelUrl}
          setShowPublicWarning={setShowPublicWarning}
          showPublicWarning={showPublicWarning}
          publicWarningDismissed={publicWarningDismissed}
        />
      </Route>
    </>
  ) : null;
};

export default MatrixChartContainer;
